import React, { useMemo } from 'react';
import { Box, Heading } from '../ui';
import type { LoadEvent, TimeData } from '../core/TimeData';
import type { Column } from 'react-table';
import { useTable } from 'react-table';
import SimpleReactTable from '../ui/Table/SimpleReactTable';

interface CargoSummaryProps {
  allLoadEvents: TimeData<LoadEvent>[];
}

interface CargoSummaryRowData {
  title: string;
  amount:
    | { mass: number | string | null; volume: number | string | null }
    | string
    | null;
}

const CargoSummary = ({ allLoadEvents }: CargoSummaryProps): JSX.Element => {
  let totalAmountInquiryMass = 0;
  let totalAmountCoaPrelimsMass = 0;
  let totalAmountFixedSpotMass = 0;
  let totalAmountConfirmedCoaMass = 0;

  let totalAmountInquiryVolume = 0;
  let totalAmountCoaPrelimsVolume = 0;
  let totalAmountFixedSpotVolume = 0;
  let totalAmountConfirmedCoaVolume = 0;

  let totalOfAllMass = 0;
  let totalOfAllVolume = 0;

  allLoadEvents.forEach((td) => {
    const incomingVoyageCharter =
      td.event.order.incomingVoyageCharterRelation
        ?.voyageCharterByVoyageCharterId;

    const isCoa =
      (incomingVoyageCharter?.voyageCharterToContractsByVoyageCharterId.nodes.at(
        0,
      )?.coaId ?? null) !== null;

    const isSpotInquiry = !isCoa && incomingVoyageCharter?.isInquiry;

    const isCoaConfirmed = isCoa && !incomingVoyageCharter?.isPreliminary;

    const isFixedSpot = !isCoa && !incomingVoyageCharter?.isInquiry;

    const isCoaPreliminary = isCoa && incomingVoyageCharter?.isPreliminary;

    if (isSpotInquiry) {
      totalAmountInquiryMass += td.mass;
      totalAmountInquiryVolume += td.holdVolume;
    }

    if (isCoaPreliminary) {
      totalAmountCoaPrelimsMass += td.mass;
      totalAmountCoaPrelimsVolume += td.holdVolume;
    }

    if (isFixedSpot) {
      totalAmountFixedSpotMass += td.mass;
      totalAmountFixedSpotVolume += td.holdVolume;
    }

    if (isCoaConfirmed) {
      totalAmountConfirmedCoaMass += td.mass;
      totalAmountConfirmedCoaVolume += td.holdVolume;
    }

    totalOfAllMass += td.mass;
    totalOfAllVolume += td.holdVolume;
  });

  const percentOfSpotMass = totalOfAllMass
    ? (
        ((totalAmountInquiryMass + totalAmountFixedSpotMass) / totalOfAllMass) *
        100
      ).toFixed(0) + '%'
    : null;
  const percentOfCoaMass = totalOfAllMass
    ? (
        ((totalAmountCoaPrelimsMass + totalAmountConfirmedCoaMass) /
          totalOfAllMass) *
        100
      ).toFixed(0) + '%'
    : null;

  const percentOfSpotVolume = totalOfAllVolume
    ? (
        ((totalAmountInquiryVolume + totalAmountFixedSpotVolume) /
          totalOfAllVolume) *
        100
      ).toFixed(0) + '%'
    : null;

  const percentOfCoaVolume = totalOfAllVolume
    ? (
        ((totalAmountCoaPrelimsVolume + totalAmountConfirmedCoaVolume) /
          totalOfAllVolume) *
        100
      ).toFixed(0) + '%'
    : null;

  const cargoSummaryData: CargoSummaryRowData[] = [
    {
      title: 'Total amount of inquiry cargoes',
      amount: {
        mass: totalAmountInquiryMass,
        volume: totalAmountInquiryVolume,
      },
    },
    {
      title: 'Total amount of COA preliminary cargoes',
      amount: {
        mass: totalAmountCoaPrelimsMass,
        volume: totalAmountCoaPrelimsVolume,
      },
    },
    {
      title: 'Total amount of fixed spot cargoes',
      amount: {
        mass: totalAmountFixedSpotMass,
        volume: totalAmountFixedSpotVolume,
      },
    },
    {
      title: 'Total amount of confirmed COA cargoes',
      amount: {
        mass: totalAmountConfirmedCoaMass,
        volume: totalAmountConfirmedCoaVolume,
      },
    },
    {
      title: 'Percentage of spot cargoes (inquiries + fixed, % of total)',
      amount: { mass: percentOfSpotMass, volume: percentOfSpotVolume },
    },
    {
      title: 'Percentage of COA cargoes (prelims and confirmed, % of total)',
      amount: { mass: percentOfCoaMass, volume: percentOfCoaVolume },
    },
    {
      title: 'Total amount of all cargoes',
      amount: { mass: totalOfAllMass, volume: totalOfAllVolume },
    },
  ];

  const cargoSummaryColumns: Array<Column<CargoSummaryRowData>> = useMemo(
    () => [
      { Header: 'Title', accessor: 'title' },
      {
        Header: 'Amount (Mts, cbm)',
        accessor: (row) => {
          if (typeof row.amount === 'string' || row.amount === null) {
            return row.amount;
          } else {
            const { mass, volume } = row.amount;

            const formattedMass =
              mass !== null
                ? typeof mass === 'number'
                  ? `${mass.toFixed(0)} mts`
                  : `${mass} (mts)`
                : null;

            const formattedVolume =
              volume !== null
                ? typeof volume === 'number'
                  ? `${volume.toFixed(0)} cbm`
                  : `${volume} (cbm)`
                : null;
            return `${formattedMass}, ${formattedVolume}`;
          }
        },
      },
    ],
    [],
  );

  const cargoSummaryTable = useTable<CargoSummaryRowData>({
    columns: cargoSummaryColumns,
    data: cargoSummaryData,
  });

  cargoSummaryTable.footerGroups = [];

  return (
    <Box p={4}>
      <Heading mb={4} fontWeight="bold" fontSize={4}>
        Voyage cargo summary
      </Heading>
      <SimpleReactTable table={cargoSummaryTable} />
    </Box>
  );
};

export default CargoSummary;
